import React, { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Layout = () => {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const [openMenu, setOpenMenu] = useState(null);

    const role = (user?.role || localStorage.getItem("role") || "").toLowerCase();

    const isAdmin = role === "admin" || role === "administrator";
    const isClient = role === "client" || role === "user";
    const isEmployee = role === "employee";

    const baseMenu = [
        { label: "Dashboard", path: "dashboard" },
        { label: "Profile", path: "profile" },
        { label: "2FA", path: "enable-2fa" },
    ];

    const adminGroups = [
        {
            title: "AI",
            items: [
                ["🤖 AI Command Center", "admin/ai-command-center"],
                ["🚚 Predictive Logistics", "admin/predictive-logistics"],
                ["⭐ Trust Engine v2", "admin/intelligent-trust-engine"],
                ["📈 AI Prediction Center", "admin/ai-prediction-center"],
                ["📊 Demand Forecast", "admin/demand-forecast"],
            ],
        },
        {
            title: "IoT",
            items: [
                ["📍 IoT Map", "admin/iot-map"],
                ["📡 IoT Devices", "admin/iot-devices"],
                ["📡 IoT Control Center", "admin/iot-control-center"],
                ["🛰 Global Monitoring", "admin/global-monitoring"],
            ],
        },
        {
            title: "Blockchain",
            items: [
                ["⛓ Blockchain Explorer", "admin/blockchain-explorer"],
                ["⛓ Blockchain Logs", "admin/security/blockchain-logs"],
                ["📜 Smart Contracts", "admin/smart-contracts"],
                ["🧾 Trust History", "admin/trust-history"],
            ],
        },
        {
            title: "Logistics",
            items: [
                ["🚀 Mission Control", "admin/mission-control"],
                ["🌍 Global Operations", "admin/global-operations"],
                ["📦 Package Scan", "admin/package-scan"],
                ["🏭 Warehouses", "admin/warehouses"],
                ["🚛 Drivers", "admin/drivers"],
                ["🚦 Dispatch Centers", "admin/dispatch-centers"],
            ],
        },
        {
            title: "Security",
            items: [
                ["🔐 Security Center", "admin/security-center"],
                ["🛡 Security Dashboard", "admin/security"],
                ["🔑 Encryption Keys", "admin/encryption"],
                ["🔐 Adaptive Security", "admin/adaptive-security"],
                ["🧾 System Logs", "admin/security/log-viewer"],
                ["⚠ Anomaly Detection", "admin/security/anomaly-detection"],
                ["📈 Anomaly Prediction", "admin/security/anomaly-prediction"],
            ],
        },
        {
            title: "Administration",
            items: [
                ["🧭 Admin Panel", "admin-panel"],
                ["👥 Clients", "admin/clients"],
                ["📦 Shipments", "admin/shipments"],
                ["👤 Users", "admin/users"],
                ["🔔 Notifications", "admin/notifications"],
                ["🏢 Executive Dashboard", "admin/executive-dashboard"],
                ["🧩 Plugin Manager", "admin/platform-modules"],
                ["🏷 Cargo Types", "admin/cargo-types"],
            ],
        },
    ];

    const clientMenu = [
        ["Client Form", "client-form"],
        ["My Orders", "client/orders"],
        ["Create Order", "client/orders/create"],
        ["My Shipments", "client/shipments"],
        ["My Invoices", "client/invoices"],
        ["IoT Tracking", "client/iot-tracking"],
        ["Payments", "payments"],
        ["Delivery Price", "client/delivery-price"],
        ["GeoFence", "client/geofence"],
    ];

    const employeeMenu = [
        ["Employee Dashboard", "employee/dashboard"],
        ["Invoices", "employee/invoices"],
        ["Create Invoice", "employee/invoices/create"],
        ["Package Scan", "employee/package-scan"],
        ["Tracking Numbers", "employee/tracking-numbers"],
        ["Delivery Price", "employee/delivery-price"],
        ["Delivery History", "employee/delivery-history"],
        ["Delivery Routes", "employee/delivery-routes"],
    ];

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-slate-100">
            <header className="sticky top-0 z-50 bg-slate-950 text-white shadow-lg">
                <div className="max-w-screen-2xl mx-auto px-6 py-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <Link
                            to="/dashboard"
                            className="text-xl font-black text-white mr-4"
                        >
                            🛡 IDLogicChain
                        </Link>

                        {baseMenu.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className="px-4 py-2 rounded-lg hover:bg-white/10 transition"
                            >
                                {item.label}
                            </Link>
                        ))}

                        {isAdmin &&
                            adminGroups.map((group) => (
                                <div key={group.title} className="relative">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setOpenMenu(openMenu === group.title ? null : group.title)
                                        }
                                        className="px-4 py-2 rounded-lg hover:bg-white/10 transition"
                                    >
                                        {group.title} ▾
                                    </button>

                                    {openMenu === group.title && (
                                        <div className="absolute left-0 mt-2 w-72 rounded-xl bg-white text-slate-900 shadow-2xl border border-slate-200 overflow-hidden">
                                            {group.items.map(([label, path]) => (
                                                <Link
                                                    key={path}
                                                    to={path}
                                                    onClick={() => setOpenMenu(null)}
                                                    className="block px-4 py-3 hover:bg-slate-100 transition text-sm"
                                                >
                                                    {label}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                        {isClient &&
                            clientMenu.map(([label, path]) => (
                                <Link
                                    key={path}
                                    to={path}
                                    className="px-4 py-2 rounded-lg hover:bg-white/10 transition"
                                >
                                    {label}
                                </Link>
                            ))}

                        {isEmployee &&
                            employeeMenu.map(([label, path]) => (
                                <Link
                                    key={path}
                                    to={path}
                                    className="px-4 py-2 rounded-lg hover:bg-white/10 transition"
                                >
                                    {label}
                                </Link>
                            ))}

                        <button
                            type="button"
                            onClick={handleLogout}
                            className="ml-auto px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 transition"
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-screen-2xl mx-auto px-6 py-8">
                <Outlet />
            </main>
        </div>
    );
};

export default Layout;